<template>
  <div>
    <div class="header row">
      <div>
        <HKLGrayHint
          v-if="latestUpdateOn"
        >
          {{ latestUpdateOn }}
        </HKLGrayHint>
        <p>
          {{ $t('hint.regionConfig.limitImg') }}
        </p>
      </div>
      <div
        v-if="canEdit"
        class="btn-group"
      >
        <HKLButton
          v-if="!isEdit"
          color="orange"
          action="edit"
          @click="changeEdit"
        />
        <HKLButton
          v-if="isEdit"
          color="darkblue"
          action="cancel"
          @click="cancelEdit"
        />
        <HKLButton
          v-if="isEdit"
          color="orange"
          action="save"
          @click="saveTmpImg"
        />
      </div>
    </div>

    <div class="region-image-body">
      <div
        v-for="(item, index) in imageList"
        :key="`region-image-${index}`"
        :class="isEdit ? 'edit-mode-row' : 'view-mode-row'"
        class="row"
      >
        <div class="region-image-label small-3">
          {{ $t(`settings.regionConfig.imagesTitle.${item.label}.label`) }}
          <HKLGrayHint class="region-image-desc font-size-14">
            {{ $t(`settings.regionConfig.imagesTitle.${item.label}.desc`) }}
          </HKLGrayHint>
        </div>
        <div
          v-if="isEdit"
          class="upload-area small-5"
        >
          <HkldDropzone
            :sourceId="sourceId"
            :maxNumberOfFiles="1"
            uploadDocTypes="image/png"
            :customUrl="'region-images/upload'"
            :metaData="{ name: item.name }"
          />
        </div>
        <div
          v-if="hasImg(item.name, fullList)"
          class="region-image-uploaded-file small-3"
        >
          <a
            class="link_text"
            @click="download(item.name)"
          >
            {{ item.name }}
          </a>
          <HKLGrayHint
            v-if="fileOriginNameMap[item.name]"
            class="font-size-14"
          >
            ({{ $t('settings.regionConfig.originFileName') }}: {{ fileOriginNameMap[item.name] }})
          </HKLGrayHint>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { onMounted, computed, ref, onUnmounted, unref, watch } from 'vue-demi'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HkldDropzone from '@/controls/HkldDropzone'
import EventBus from '@/utils/eventBus'
import { downloadFile } from '@/utils/xfs'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import HKLGrayHint from '@/controls/HKLGrayHint'
import { getLastUpdatedByOn } from './utils'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import { getRegionImg, updateReginImg } from '@/api/settings/request'
import { useStoreGetters } from '../../../../store'

export default {
  components: {
    HKLButton,
    HkldDropzone,
    HKLGrayHint,
  },
  setup () {
    const dirImageList = ref([])
    const tmpList = ref([])
    const isEdit = ref(false)
    const imgHistory = ref({})
    const { currentRegion } = useStoreGetters('regions', ['currentRegion'])
    const sourceId = computed(() => {
      return `region-image-${unref(currentRegion).name}`
    })
    const hasImg = (name, list) => {
      return unref(list).includes(name)
    }
    const fileOriginNameMap = ref({})
    const imageList = computed(() => {
      return [{
        label: 'hkldLogo',
        name: 'hkld-logo.png',
      }, {
        label: 'emailLogoHkland',
        name: 'email-logo-hkland.png',
      }, {
        label: 'hklLogoEn',
        name: 'hkld-logo-en.png',
      }, {
        label: 'logo',
        name: 'logo.png',
      }, {
        label: 'hkldLogoPdf',
        name: 'hkld-logo-pdf.png',
      }, {
        label: 'logoFooterPdf',
        name: 'hkld-logo-footer-pdf.png',
      }, {
        label: 'callCenter',
        name: 'call-center.png',
      }, {
        label: 'wpNoTenant',
        name: 'wp-no-tenant.png',
      }]
    })
    const fullList = computed(() => {
      const result = lodash.concat(unref(dirImageList), unref(tmpList))
      return result
    })
    const addNewImg = (newImg) => {
      const fileName = newImg.fileName
      const originName = newImg.originFileName
      if (originName) {
        const newNameMap = lodash.cloneDeep(unref(fileOriginNameMap))
        lodash.set(newNameMap, [fileName], originName)
        fileOriginNameMap.value = newNameMap
      }

      if (fileName && !hasImg(fileName, tmpList)) {
        tmpList.value.push(fileName)
      }
    }
    const latestUpdateOn = computed(() => {
      return getLastUpdatedByOn(unref(imgHistory))
    })
    const saveTmpImg = async () => {
      const request = {fileList: unref(tmpList), originFileNames: unref(fileOriginNameMap)}
      try {
        await updateReginImg(request)
        notification.fireNotification(true, i18n.t('success'))
      } catch (err) {
        notification.handleResponseError(err)
        throw err
      }
      await resetList()
      changeEdit()
    }
    const download = async (fileName) => {
      const url = `region-images/item/${fileName}`
      let params
      if (unref(isEdit)) {
        params = { isTmp: unref(isEdit) }
      }
      await downloadFile(url, fileName, params)
    }
    const resetList = async () => {
      let { history, result } = await getRegionImg()
      dirImageList.value = result
      imgHistory.value = history
      fileOriginNameMap.value = lodash.get(history, 'detail.originFileNames', {})
      tmpList.value = []
    }
    const changeEdit = () => {
      isEdit.value = !isEdit.value
    }
    const cancelEdit = async () => {
      await resetList()
      changeEdit()
    }
    const canEdit = computed(() => {
      return aclHelper.getAccess(aclPermsType.settings.updateRegional)
    })
    watch(sourceId, (curVal, prevVal) => {
      EventBus.$off(`controls.hkldDropzone.newItem.${unref(prevVal)}`)
    })
    onMounted(async () => {
      await resetList()
      EventBus.$on(`controls.hkldDropzone.newItem.${unref(sourceId)}`, (newFile) => {
        addNewImg(newFile)
      })
    })
    onUnmounted(() => {
      EventBus.$off(`controls.hkldDropzone.newItem.${unref(sourceId)}`)
    })
    return {
      imageList,
      dirImageList,
      tmpList,
      addNewImg,
      download,
      hasImg,
      fullList,
      isEdit,
      canEdit,
      sourceId,
      changeEdit,
      cancelEdit,
      saveTmpImg,
      latestUpdateOn,
      fileOriginNameMap,

    }
  },

}
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.region-image-body {
  margin-bottom: 20px;
}

.upload-area {
  margin-right: 20px;
  margin-bottom: 20px;
}
.link_text {
  margin-right: 20px;
  text-decoration: underline;
  color: #0c3a58;
}

.view-mode-row {
  margin-bottom: 20px;
}

.region-image-label

.edit-mode-row {
  .region-image-label, .region-image-uploaded-file {
  align-content: center;
  height: 85px;
}
}

</style>
